import { Trans } from "@lingui/react/macro"
import { UnstyledButton } from "@mantine/core"
import { speakerEntityId } from "../../../lib/speaker-vm"
import { SpeakerRow } from "../SpeakerRow"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

function rowKey(label: string | null, displayName: string, entityId: string | undefined): string {
  return entityId ?? label ?? displayName
}

export function WhoSpokePanelView({
  rows,
  unnamedCount,
  speakerCount,
  showSample,
  pickerQuery,
  pickerPeople,
  pickerLoading,
  pickerSuggested = null,
  pickerUsing,
  pickerError,
  onNeedsNameClick,
  onPlaySample,
  onConfirm,
  onReject,
  onPickSomeoneElse,
  onNameSpeaker,
  onEdit,
  onMerge,
  onMergeIntoConflict,
  onPickerQueryChange,
  onPickerCommit,
  onPickerCancel,
  rowRefs,
}: IProps): React.JSX.Element {
  return (
    <section className={classes.panel} data-testid="who-spoke-panel">
      <div className={classes.head}>
        <h2 className={classes.title}>
          <Trans>Who spoke</Trans>
        </h2>
        {unnamedCount > 0 ? (
          <UnstyledButton className={classes.pill} onClick={onNeedsNameClick} data-testid="who-spoke-needs-names">
            {unnamedCount === 1 ? <Trans>1 needs a name ↓</Trans> : <Trans>{unnamedCount} need a name ↓</Trans>}
          </UnstyledButton>
        ) : null}
        <div className={classes.headRight}>
          <span>
            <Trans>{speakerCount} speakers</Trans>
          </span>
          <span>·</span>
          <span>
            <Trans>sorted by talk time</Trans>
          </span>
        </div>
      </div>
      {rows.map((row) => {
        const key = rowKey(row.speaker.label, row.speaker.displayName, speakerEntityId(row.speaker))
        return (
          <SpeakerRow
            key={key}
            speaker={row.speaker}
            displayLabel={row.displayLabel}
            statusText={row.statusText}
            confirmedByYou={row.confirmedByYou}
            voiceSaved={row.voiceSaved}
            fresh={row.fresh}
            focused={row.focused}
            evidenceLine={row.evidenceLine}
            runnerUpLine={row.runnerUpLine}
            conflictLine={row.conflictLine}
            conflictPeerLabel={row.conflictPeerLabel}
            duplicateLine={row.duplicateLine}
            mergeOptions={row.mergeOptions}
            sampleSeconds={row.sampleSeconds}
            showSample={showSample}
            pickerOpen={row.pickerOpen}
            pickerQuery={pickerQuery}
            pickerPeople={pickerPeople}
            pickerLoading={pickerLoading}
            pickerSuggested={pickerSuggested}
            pickerUsing={pickerUsing}
            pickerError={pickerError}
            confirmPending={row.confirmPending}
            rejectPending={row.rejectPending}
            mergePending={row.mergePending}
            error={row.error}
            onPlaySample={showSample ? () => onPlaySample(row.speaker) : undefined}
            onConfirm={() => onConfirm(row.speaker)}
            onReject={() => onReject(row.speaker)}
            onPickSomeoneElse={() => onPickSomeoneElse(row.speaker)}
            onNameSpeaker={() => onNameSpeaker(row.speaker)}
            onEdit={onEdit ? () => onEdit(row.speaker) : undefined}
            onMerge={(option) => onMerge(row.speaker, option)}
            onMergeIntoConflict={() => onMergeIntoConflict(row.speaker)}
            onPickerQueryChange={onPickerQueryChange}
            onPickerCommit={onPickerCommit}
            onPickerCancel={onPickerCancel}
            rowRef={(el) => {
              const map = rowRefs.current
              if (!map) return
              if (el) map.set(key, el)
              else map.delete(key)
            }}
          />
        )
      })}
    </section>
  )
}
